import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  BarChart3,
  DollarSign,
  TrendingUp,
  FileText,
  Smartphone,
  Shield,
  Zap,
  Smartphone as MobileIcon,
  ArrowRight
} from 'lucide-react';

const Home = () => {
  const features = [
    {
      icon: BarChart3,
      title: 'Real-Time Analytics',
      description: 'Track your app performance with comprehensive analytics and insights',
    },
    {
      icon: DollarSign,
      title: 'Revenue Tracking',
      description: 'Monitor your earnings, eCPM, and revenue trends across all your apps',
    },
    {
      icon: TrendingUp,
      title: 'Performance Metrics',
      description: 'Analyze impressions, clicks, fill rates, and user engagement metrics',
    },
    {
      icon: FileText,
      title: 'Custom Reports',
      description: 'Create and export detailed reports with customizable metrics and breakdowns',
    },
    {
      icon: Smartphone,
      title: 'App Management',
      description: 'Manage all your apps, ad units, and networks from a single dashboard',
    },
    {
      icon: Shield,
      title: 'Secure & Reliable',
      description: 'Enterprise-grade security with secure API integration and data protection',
    },
  ];

  const containerClass = 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8';

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary/10 via-background to-primary/5 py-20 lg:py-32">
        <div className={`${containerClass} overflow-x-hidden`}>
          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-primary/10 rounded-full text-xs sm:text-sm font-medium text-primary mb-4 sm:mb-6">
              <Zap className="w-4 h-4" />
              <span>Powered by NAndroidEx</span>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-foreground mb-4 sm:mb-6 tracking-tight">
              Your IronSource
              <span className="block text-primary">Dashboard</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-muted-foreground mb-6 sm:mb-8 leading-relaxed px-2">
              Comprehensive analytics and revenue tracking platform for IronSource publishers.
              Monitor performance, track earnings, and make data-driven decisions.
            </p>
            <div className="flex justify-center items-center">
              <Link to="/login">
                <Button size="lg" className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6 text-base sm:text-lg">
                  Get Started
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-background">
        <div className={`${containerClass} overflow-x-hidden`}>
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-foreground mb-4">
              Everything You Need to Succeed
            </h2>
            <p className="text-xl text-muted-foreground">
              Powerful features to help you monitor, analyze, and optimize your IronSource monetization
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="border-2 hover:border-primary/50 transition-colors">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-muted-foreground">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mobile Apps Coming Soon Section */}
      <section className="py-20 bg-gradient-to-br from-primary/5 to-background">
        <div className={`${containerClass} overflow-x-hidden`}>
          <div>
            <Card className="border-2 border-primary/20">
              <CardContent className="p-12 text-center">
                <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <MobileIcon className="w-10 h-10 text-primary" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                  Mobile Apps Coming Soon
                </h2>
                <p className="text-xl text-muted-foreground mb-8">
                  Take your dashboard on the go! Native iOS and Android apps are in development,
                  bringing all the power of the web dashboard to your mobile device.
                </p>
                <div className="flex flex-wrap justify-center gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Smartphone className="w-4 h-4" />
                    <span>iOS App</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Smartphone className="w-4 h-4" />
                    <span>Android App</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4" />
                    <span>Real-time Notifications</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    <span>Secure & Fast</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Home;
